<template>
  <div class="wrapper">
    <div id="sh-log" class="hidden">
      <ul>
        <li
          v-for="(row, index) in shLog"
          :key="index"
        >{{ findAsset(row.assetId).name }} | method: {{ row.method }} | result: {{ row.result }}
        </li>
      </ul>
    </div>
    <v-card class="visual-top-panel layout">
      <v-layout row wrap>
        <v-flex xs12 lg6 xl4 class="buttons-panel">
          <!--switch visual mode-->
          <!--0 = view, 1 = control, 2 = edit, 3 = replay-->
          <v-btn-toggle v-model="visualMode" class="visual-mode-switch">
            <v-btn text
                   :title="$t('visuals.watch-mode')"
                   value="watch"
            >
              <v-icon>remove_red_eye</v-icon>
            </v-btn>
            <v-btn
              text
              v-if="$can('dispatcher')"
              :disabled="!commandToken"
              :title="$t('visuals.command-mode')"
              value="command"
            >
              <v-icon>settings_remote</v-icon>
            </v-btn>
            <v-btn text
                   v-if="isDesktop"
                   :disabled="!$can('admin')"
                   :title="$t('visuals.edit-mode')"
                   value="edit"
                   data-test="visual-edit-mode"
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-btn text
                   :title="$t('visuals.replay-mode')"
                   value="replay"
            >
              <v-icon>update</v-icon>
            </v-btn>
          </v-btn-toggle>

          <!--zoom in and out-->
          <v-menu auto offset-y :nudge-width="300" :close-on-content-click="false">
            <template v-slot:activator="{ on }">
              <v-btn icon class="mt-3 ml-3" v-on="on" :title="$t('zoom')" data-test="visual-zoom">
                <v-icon>zoom_in</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-text>
                <v-slider
                  v-model="zoomSlide"
                  :step="zoomSlideStep"
                  data-test="visual-zoom-slider"
                  hide-details
                ></v-slider>
              </v-card-text>
            </v-card>
          </v-menu>
          <!--edit, no selection-->
          <div v-if="visualMode === 'edit' && selectionIds.length === 0" class="ret-v-if-wrapper">
            <!--add element by type-->
            <v-menu bottom offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on" :title="$t('Add element by type')" data-test="select">
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-card-text>
                  <v-autocomplete
                    v-model="addingElementTypeName"
                    item-text="name"
                    item-value="name"
                    :items="elementTypes"
                    :disabled="!editedLevelId"
                    :placeholder="$t('Element type')"
                    data-test="select-element"
                  />
                </v-card-text>
              </v-card>
            </v-menu>

            <!--add connected asset-->
            <v-menu bottom offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on" :title="$t('Add asset')">
                  <v-icon>cast_connected</v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-card-text>
                  <v-autocomplete
                    v-model="addingConnectedAssetId"
                    :items="getUnusedAssetsData()"
                    :disabled="!editedLevelId"
                    :placeholder="$t('Asset')"
                  />
                </v-card-text>
              </v-card>
            </v-menu>
          </div>

          <h2 class="mt-3 ml-4">{{ visual.name }}</h2>
        </v-flex>

        <!--view or control, single element selected-->
        <v-flex xs12 lg6 xl4 v-if="(visualMode === 'watch' || visualMode === 'command') && selectedElement">
          <!--display asset route-->
          <div class="ret-static-group">
            <v-icon
              class="ret-group-prepend-icon"
              :color="isSelectedElementConnected ? 'primary' : null"
            >cast_connected
            </v-icon>
            <span class="ret-static-text">{{selectedElementAssetRoute}}</span>
          </div>
        </v-flex>

        <!--edit, single element selected-->
        <v-flex xs12 lg6 xl4 v-if="visualMode === 'edit' && selectedElement">
          <!--connect / disconnect asset-->
          <div class="ret-treeselect-group">
            <v-autocomplete v-model="selectedAssetId" :items="getUnusedAssetsData()" class="pt-0">
              >
              <template v-slot:prepend>
                <v-icon :color="isSelectedElementConnected ? 'primary' : 'gray'">cast_connected</v-icon>
              </template>
            </v-autocomplete>
          </div>
        </v-flex>

        <!--replay-->
        <v-flex xs12 lg6 xl4 v-if="visualMode === 'replay'" class="d-flex">
          <date-range-picker v-model="replayRange" :loading="replay.loading" class="picker"></date-range-picker>

          <v-row align="center">
            <v-btn fab x-small color="blue" class="mx-1 white--text"
                   :disabled="!replay.loadEnabled || replay.loading"
                   @click="replayLoad"
                   :title="$t('reload')">
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-row>
        </v-flex>

        <!--devel, single element selected-->
        <v-flex xs12 v-if="$devel() && selectedElement">
          <p class="mt-3 mb-0">Selected element: {{ selectedElement }}</p>
          <p class="mt-3 mb-0">Asset data: {{ selectedElementAssetData }}</p>
        </v-flex>
      </v-layout>

      <div v-if="visualMode === 'edit'" class="hidden-md-and-down">
        <transition name="toggle-rotate" mode="out-in">
          <v-btn text icon :key="rightPanel" @click="rightPanel = !rightPanel" data-test="edit-dialog">
            <v-icon>{{rightPanel ? 'expand_less' : 'expand_more'}}</v-icon>
          </v-btn>
        </transition>
      </div>

      <v-tooltip left v-if="serverProblem">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" text icon color="red" class="mt-2" @click="serverProblemSnackbar = true">
            <v-icon>error</v-icon>
          </v-btn>
        </template>
        {{ $t('offline') }}
      </v-tooltip>
    </v-card>

    <div id="stage-parent" class="stage-parent">
      <div id="container" class="canvas" @wheel.ctrl.prevent="onZoom"></div>
    </div>

    <asset-and-alarms
      v-if="isDesktop && (visualMode === 'watch' || visualMode === 'command')"
      :visualMode="visualMode"
      :selected-element="selectedElement"
      :selected-element-asset-route="selectedElementAssetRoute"
      @select-asset="selectAsset"
      @deselect-asset="selectionIds = []"
    />

    <transition name="editor-panel-slide">
      <frame-visual-editor-panel
        v-if="dataLoaded && visualMode === 'edit' && rightPanel"
        :visual="visual"
        :zoom-level="zoomLevel"
        :reload="storeUpdate"
        :edited-levels="editedLevels"
        :reselect-edited-level="reselectEditedLevel"
        :displayed-levels="displayedLevels"
        :reselect-displayed-levels="reselectDisplayedLevels"
        :selected-element="selectedElement"
        :update-many-elements="updateManyElements"
        class="hidden-md-and-down"
      />
    </transition>

    <transition name="editor-panel-slide">
      <replay-viewer
        v-if="visualMode === 'replay' && replay.loaded"
        :from="replayRange.from"
        :to="replayRange.to"
        :items-count="replay.data.length"
        :item-index="replay.itemIndex"
        :diffs="replay.data[replay.itemIndex].diffs"
        :prev-item-time="(replay.data[replay.itemIndex - 1] || {}).t"
        :next-item-time="(replay.data[replay.itemIndex + 1] || {}).t"
        @time="replayResetTimeTo"
      />
    </transition>

    <!--Mobile display of alarms -->
    <v-bottom-sheet v-model="sheet" v-if="isMobile">
      <asset-and-alarms
        :visual-mode="visualMode"
        :selected-element="selectedElement"
        :selected-element-asset-route="selectedElementAssetRoute"
        @select-asset="selectAsset"
        @deselect-asset="selectionIds = []"
      />
    </v-bottom-sheet>

    <v-bottom-sheet
      :value="!sheet && !serverProblemSnackbar"
      hide-overlay
      full-width
      v-if="isMobile && (visualMode === 'watch' || visualMode === 'command')"
    >
      <v-btn
        block
        text
        @click="sheet = true"
      >{{ $t('alarms') }} {{ (selectedElement ? `& ${$t('assets.detail')}` : '') }}
      </v-btn>
    </v-bottom-sheet>

    <v-snackbar bottom v-model="commandSnack">
      {{ $t('visuals.exit-command') }}
      <v-btn small text icon dark @click="commandSnack = false">
        <v-icon>clear</v-icon>
      </v-btn>
    </v-snackbar>

    <v-snackbar bottom v-model="websocketConnecting">{{ $t('trying-to-connect') }}</v-snackbar>

    <v-snackbar bottom v-model="shLogUpdated" :timeout="10000">{{ lastShLogRecord() }}</v-snackbar>

    <v-snackbar bottom v-model="serverProblemSnackbar">
      {{ $t('offline') }}
      <v-spacer/>
      <v-btn dark text @click.native="resolveServerProblem">{{ $t('reconnect') }}</v-btn>
      <v-btn small text icon dark @click="serverProblemSnackbar = false">
        <v-icon>clear</v-icon>
      </v-btn>
    </v-snackbar>
  </div>
</template>

<style scoped>
.wrapper {
  position: relative;
}

.visual-top-panel {
  padding: 5px 20px 8px;
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
  opacity: 0.92;
}

.buttons-panel {
  display: flex;
  align-items: flex-start;
}

.visual-mode-switch {
  margin-top: 6px;
}

.ret-v-if-wrapper {
  display: inline-block;
}

.hidden {
  display: none;
}

div.stage-parent {
  width: 100%;
  height: 100%;
  position: relative; /*Needed for the zoom-panel absolute positioning to work*/
}

.editor-panel-slide-enter-active {
  transition: all 0.3s;
}

.editor-panel-slide-leave-active {
  transition: all 0.3s;
}

.editor-panel-slide-enter,
.editor-panel-slide-leave-to {
  transform: translateY(-800px);
}

.toggle-rotate-enter-active {
  transition: all 0.5s;
}

.toggle-rotate-enter {
  transform: rotate(180deg);
}

.toggle-rotate-leave-active {
  transition: all 0s;
}

.picker {
  width: 390px;
}
</style>

<script>
import RenStage, { minZoomLevel, maxZoomLevel } from '../components/ren-stage.es6'
import AssetAndAlarms from '../../alarms/asset-and-alarms.vue'
import FrameVisualEditorPanel from './frame-visual-editor-panel.vue'
import initWebsocket from '../../_initiators/websocket.es6'
import { linearTransform } from '../../history/utils'
import DateRangePicker from '../../shared/components/date-range-picker.vue'
import ReplayViewer from '../components/replay-viewer.vue'

const zoomSlidePowerBase = 1.0455610964243 // to ensure slide 50 converts to zoomLevel 1
const power = number => Math.pow(zoomSlidePowerBase, number)
const zoomSlideStep = 1

export default {
  components: {
    AssetAndAlarms,
    FrameVisualEditorPanel,
    DateRangePicker,
    ReplayViewer
  },
  props: {
    id: {
      required: true
    },
    initVisualMode: {
      type: String,
      default: 'watch'
    }
  },
  data () {
    const now = new Date().getTime()
    return {
      model: { name: 'visuals' },
      dataLoaded: false,
      visualMode: this.initVisualMode,
      selectionIds: [], // Array of IDs of selected elements
      selectedAssetId: null,
      addingElementTypeName: null,
      addingConnectedAssetId: null,
      commandToken: this.$cookies.get('command'),
      commandTo: null,
      commandSnack: false,
      assets: [],
      zoomSlideStep,
      stage: undefined,
      stageX: undefined,
      stageY: undefined,
      zoomSlide: 50,
      prevZoomSlide: 50,
      zoomLevel: 1,
      posX: 1,
      posY: 1,
      sizeAdjustmentSlide: 9,
      sizeAdjustmentSlideStep: 1,
      editedLevels: [],
      editedLevelId: null,
      displayedLevels: [],
      displayedLevelIds: [],
      rightPanel: true,
      websocket: {},
      websocketOffline: true,
      websocketConnecting: false,
      shLogUpdated: false,
      sheet: false,
      serverProblemSnackbar: false,
      replayRange: {
        from: now - 20 * 60 * 1000,
        to: now
      },
      replay: {
        loadEnabled: true,
        loaded: false,
        loading: false,
        data: [],
        itemIndex: 0
      }
    }
  },
  // Custom attributes (accessible via this.$options.xxx)
  renStage: undefined,
  created () {
    this.storeUpdate()
  },
  mounted () {
    this.prevEditedLevels = []
  },
  beforeDestroy () {
    // Destroy the renStage
    this.$options.renStage.destroy()
    this.closeWebsocket()
  },
  computed: {
    visual () { // Object
      return this.$store.state.item.visuals
    },
    shLog () {
      return this.$store.state.shLog
    },
    elements () { // Array
      if (this.visualMode === 'edit') {
        if (!this.editedLevelId) {
          return []
        }
        return this.filterElements(({ levelId }) => levelId === this.editedLevelId)
      }
      return this.filterElements(({ from, to }) =>
        (!from || from <= this.zoomLevel) && (!to || this.zoomLevel < to))
    },
    backgroundElements () {
      return this.visualMode !== 'edit' ? [] : this.filterElements(({ id, levelId }) =>
        levelId !== this.editedLevelId && this.displayedLevelIds.includes(id))
    },
    assetData () { // Object
      return this.$store.state.assetData
    },
    assetIds () {
      const assetIds = []
      const { levelsInVisuals } = this.visual
      for (let { level } of levelsInVisuals) {
        for (let { assetId } of level.elements) {
          if (assetId) assetIds.push(assetId)
        }
      }
      return assetIds
    },
    deviceTypes () { // Object
      const types = {}
      for (let deviceType of this.$store.state.deviceTypes) {
        types[deviceType.name] = deviceType
      }
      return types
    },
    serverProblem () {
      return !this.$store.state.shConnectionStatus || (this.websocketOffline && this.visualMode !== 'replay')
    },
    shOffline () {
      return !this.$store.state.shConnectionStatus
    },
    selectedElement () { // Object or undefined
      if (this.selectionIds.length === 1) {
        return this.elements.find(element => element.id === this.selectionIds[0])
      }
      return undefined
    },
    selectedElementAssetRoute () {
      const { selectedElement } = this
      if (selectedElement && selectedElement.asset) {
        const asset = this.assets.filter(asset => asset.id === selectedElement.assetId).shift()
        return asset.fullPath
      }
      return undefined
    },
    selectedElementAssetData () {
      return this.assetData[this.selectedElement.assetId]
    },
    isSelectedElementConnected () {
      return !!this.selectedElementAssetData
    },
    bottomSheet () {
      return !!this.selectedElement
    },
    editMode () {
      return this.visualMode === 'edit'
    },
    elementTypes () { // Array
      return [...this.$store.state.items['element-types']]
    },
    isDesktop () {
      return this.$store.state.userAgent.isDesktop
    },
    isMobile () {
      return this.$store.state.userAgent.isMobile
    },
    editedLevelIndex () {
      return this.visual.levelsInVisuals
        .findIndex(level => level.levelId === this.editedLevelId)
    },
    sizeAdjustment () {
      return linearTransform(
        { from: 0, to: 100 },
        { from: 0.1, to: 10 },
        this.sizeAdjustmentSlide
      )
    },
    zoomSlideController () {
      return this.zoomSlide
    }
  },
  methods: {
    async storeUpdate () {
      console.log('[frame-visual][storeUpdate]')
      try {
        await Promise.all([
          this.$store.dispatch('getItem', { modelName: this.model.name, id: this.id }),
          this.$store.dispatch('getStaticItems', { model: { name: 'assets' } }),
          this.$store.dispatch('getItems', { model: { name: 'element-types' } }),
          this.$store.dispatch('getItems', { model: { name: 'levels' } })
        ])

        this.assets = [...this.$store.state.staticItems['assets']]
        this.dataLoaded = true
        if (this.visual.zoom !== null) {
          this.zoomSlide = this.visual.zoom
        }
        // pre-select all levels as displayed in edit mode
        this.displayedLevelIds = this.visual.levelsInVisuals
          .map(({ id }) => id)
        this.reselectDisplayedLevels()

        // pre-select first level for editing
        if (this.visual.levelsInVisuals.length > 0) {
          this.editedLevelId = this.visual.levelsInVisuals[0].levelId
          this.reselectEditedLevel()
        }

        if (!this.$store.state.useDemoDataInVisual) {
          // Must be called after the visual (and all elements are loaded)
          // so that no initial data sent via websocket are lost
          this.connectWebsocket()
        }
      } catch (error) {
        console.error(error)
      }
    },
    connectWebsocket () {
      this.websocketConnecting = true

      this.closeWebsocket()

      this.websocket = initWebsocket({ vm: this, hostname: this.$hostname })

      this.websocket.onopen = () => {
        this.websocketConnecting = false
        this.websocketOffline = false
        // If succeeded, get initial asset data
        // this.$store.dispatch('getAssetsInitValues', {visualId: this.id})
      }

      this.websocket.onclose = () => {
        this.$store.commit('emptyAssetData')
        this.$options.renStage.resetAllElements()

        this.websocketConnecting = false
        this.websocketOffline = true
      }
    },

    resolveServerProblem () {
      if (this.websocketOffline) {
        this.connectWebsocket()
      } else {
        this.websocket.send('sh-reconnect')
      }
    },
    closeWebsocket () {
      if (this.websocket.close) {
        this.websocket.onclose = () => { }
        this.websocket.close()
        this.websocketOffline = true
      }
    },
    lastShLogRecord () {
      const [row] = this.shLog
      return row ? `${this.findAsset(row.assetId).name} | ${this.$t('command')}: ${row.method} | ${this.$tc('result', 1)}: ${row.result}` : ''
    },

    filterElements (filter) {
      const { levelsInVisuals } = this.visual
      if (!levelsInVisuals) {
        return []
      }
      const elementArrays = levelsInVisuals
        .filter(filter)
        .map(levelInVisual => levelInVisual.level.elements)
      return [].concat(...elementArrays)
    },
    initRenStage () {
      if (this.$options.renStage || !document.getElementById('container')) {
        return
      }
      if (this.visual.positionX && this.visual.positionY !== null) {
        this.stageX = +this.visual.positionX
        this.stageY = +this.visual.positionY
        // console.log('%c Load x: ' + this.stageX, 'color: red; font-size: 20px;  font-weight: bold;')
        // console.log('%c Load y: ' + this.stageY, 'color: red; font-size: 20px;  font-weight: bold;')
      } else {
        this.stageX = this.posX
        this.stageY = this.posY
        // console.log('%c Load x: ' + this.stageX, 'color: red; font-size: 20px;  font-weight: bold;')
        // console.log('%c Load y: ' + this.stageY, 'color: red; font-size: 20px;  font-weight: bold;')
      }
      this.$options.renStage = new RenStage(
        {
          // Konva properties
          container: 'container', // id of container <div>
          width: 800,
          height: 600,
          x: this.stageX,
          y: this.stageY,
          scaleX: 1,
          scaleY: 1,
          draggable: true
        },
        {
          // Ren properties
          _assetData: this.assetData,
          _deviceTypes: this.deviceTypes,
          _selectionIds: this.selectionIds,
          _visualMode: this.visualMode,
          _editMode: this.editMode,
          _eventsParent: this,
          sizeAdjustment: this.sizeAdjustment,
          currentVisual: this.visual
        },
        {
          // Ren events
          handleElementsSelected: this.onElementsSelected,
          handleUpdateManyElements: this.updateManyElements,
          handleDestroyManyElements: this.destroyManyElements,
          handleZoomLevelChanged: this.updateZoomLevel
        }
      )
    },
    getUnusedAssetsData () {
      const { selectedElement } = this
      const selectedDeviceType = selectedElement ? selectedElement.deviceTyp : null
      const hasChildren = (parent) => this.assets.some(asset => asset.parentId === parent.id)
      const hasDeviceType = (asset) => (selectedDeviceType && selectedDeviceType !== 'Generic' && selectedDeviceType !== asset.deviceType)
      return this.assets.map(asset => ({
        text: asset.fullPath,
        value: asset.id,
        disabled: !hasChildren(asset) && (hasDeviceType(asset) || !!this.findElementByAsset(asset.id))
      }))
    },
    getCompatibleElementTypes (typ) {
      // Accepts Device.typ: string
      // Returns an array of compatible elementTypes (empty array if none)
      return this.elementTypes.filter(et => et.deviceTyp === typ)
    },
    findElementByAsset (assetId) {
      return this.elements
        .find(element => element.assetId === assetId)
    },
    selectAsset (assetId) {
      const element = this.findElementByAsset(assetId)
      if (element) {
        this.selectionIds = [element.id]
      }
    },
    findAsset (assetId) {
      const { name, id } = this.assets.filter(asset => asset.id === assetId).shift() || { name: 'none', id: 0 }
      return { name, id }
    },
    updateManyElements (elements) {
      this.$store.dispatch('updateVisualElements', { visual: this, elements })
    },
    destroyManyElements (elementIds) {
      let promises = []
      for (let id of elementIds) {
        promises.push(this.$store.dispatch('destroyItem', { model: { name: 'elements' }, id: id }))
      }
      return Promise.all(promises)
        .then(() => {
          this.$store.dispatch('getItem', { modelName: 'visuals', id: this.id })
          this.selectionIds = []
        })
    },
    onZoom (e) {
      const zoomFactor = Math.max(0.1, 1 - e.deltaY / 200)
      this.$options.renStage.performZoom(zoomFactor, true)
    },
    updateZoomLevel (zoomLevel) {
      this.zoomLevel = zoomLevel

      const convert = number => Math.log(linearTransform(
        { from: minZoomLevel, to: maxZoomLevel },
        { from: power(0), to: power(100) },
        number)) / Math.log(zoomSlidePowerBase)
      this.prevZoomSlide = this.zoomSlide = Math.round(convert(zoomLevel) / zoomSlideStep) * zoomSlideStep
    },
    onElementsSelected (elementIds, toggle) { // (Array, boolean)
      const existingNotRemoved = this.selectionIds.filter(id => !elementIds.includes(id))
      const newAdded = elementIds.filter(id => !this.selectionIds.includes(id))
      this.selectionIds = toggle ? [...existingNotRemoved, ...newAdded] : [...elementIds]
      this.sheet = true
    },
    callAssetMethod () {
      const { selectedElement, command, commandToken } = this
      const { assetId } = selectedElement
      this.$store.dispatch('callAssetMethod', { assetId, method: command.method, commandToken })
    },
    reselectEditedLevel () {
      this.editedLevels = []
      if (this.editedLevelIndex > -1) {
        this.editedLevels[this.editedLevelIndex] = this.editedLevelIndex
      }
      this.prevEditedLevels = []
    },
    reselectDisplayedLevels () {
      this.displayedLevels = this.visual.levelsInVisuals
        .map(({ id }) => this.displayedLevelIds.includes(id))
    },
    confirmCommand (command) {
      this.command = command
      this.confirmCommandDialog = true
    },
    cancelConfirmCommandDialog () {
      this.command = null
      this.confirmCommandDialog = false
    },
    confirmConfirmCommandDialog () {
      this.callAssetMethod()
      this.cancelConfirmCommandDialog()
    },
    exitCommandMode () {
      this.visualMode = 'watch'
      this.commandSnack = true
    },
    resetCommandTo ({ clear = true, reset = true }) {
      if (clear) {
        clearTimeout(this.commandTo)
      }
      if (reset) {
        this.commandTo = setTimeout(this.exitCommandMode, 5 * 60 * 1000)
      }
    },
    async replayLoad () {
      if (!this.websocketOffline) {
        this.closeWebsocket()
      }
      const { from, to } = this.replayRange
      // load data for each asset
      const promises = this.assetIds
        .map((id) => {
          return new Promise((resolve) => {
            this.$store.dispatch('getLogsExport', {
              assetId: id,
              timeFrom: from,
              timeTo: to
            })
              .then((data) => resolve({ id, data }))
              .catch((error) => resolve({ id, error }))
          })
        })

      try {
        this.replay.loading = true
        // load raw data + sanitize (remove error and empty)
        const data = (await Promise.all(promises))
          .filter((asset) => (asset.data && asset.data.variables))

        // consolidate data
        this.replay.data = this.createTimeline(data)
        this.replay.loaded = true
        this.$store.state.snackbar = data.length
          ? { message: this.$t('visuals.replay-loaded') }
          : { message: this.$t('visuals.replay-nodata') }
        // load first frame
        this.replay.itemIndex = 0
        this.replayUpdate()
      } catch (error) {
        console.error('[replay][load]', error)
        this.replay.loaded = false
      } finally {
        this.replay.loading = false
      }
    },
    replayResetTimeTo (time) {
      const { data } = this.replay
      this.replay.itemIndex = (data.length > 0 && time >= data[data.length - 1].t)
        ? data.length - 1 // time after last: take last
        : Math.max(0, data.findIndex(({ t }) => t > time) - 1) // find item with highest `t` <= `time`
      this.replayUpdate(true)
    },
    replayUpdate (all = false) {
      const { itemIndex, data } = this.replay
      const feed = data[itemIndex]

      for (let id in feed.assets) {
        // feed data to store to enable changing the stage while keeping replay data
        let { variables } = feed.assets[id]
        this.$store.commit('updateAssetData', { id, variables })
      }

      this.$options.renStage.updateElementsFromFeed(feed.assets)
    },
    createTimeline (entries) {
      const assetIds = new Set()
      const timeline = {}
      for (const { id, data } of entries) {
        const { name, fullPath, deviceTyp } = data
        assetIds.add(id)
        for (const variable in data.variables) {
          for (const [t, v] of data.variables[variable].values) {
            if (timeline[t] === undefined) {
              timeline[t] = {}
            }
            if (timeline[t][id] === undefined) {
              timeline[t][id] = {
                name,
                fullPath,
                deviceTyp,
                variables: {
                  online: true
                }
              }
            }
            timeline[t][id].variables[variable] = v
          }
        }
      }

      const data = []
      for (const t in timeline) {
        data.push({ t: Number(t), assets: timeline[t] })
      }
      data.sort((a, b) => a.t - b.t) // asc by time

      const { from } = this.replayRange
      if (data.length && data[0].t > from) {
        // only assets without snapshots: add empty first item at the time `from`
        data.splice(0, 0, { t: from, assets: {} })
      }
      assetIds.forEach(assetId => {
        if (!data[0].assets[assetId]) { // all assets have by default no data (gray point)
          data[0].assets[assetId] = { variables: { online: null } }
        }
      })

      this.calculateFullData(data)
      this.calculateDiffs(data)
      return data
    },
    calculateFullData (data) { // have full data at each time instant
      let prevItem
      for (const item of data) {
        if (prevItem) {
          for (const [id, asset] of Object.entries(prevItem.assets)) {
            if (!item.assets[id]) {
              item.assets[id] = asset
            }
          }
        }
        prevItem = item
      }
    },
    calculateDiffs (data) {
      let prevItem
      for (const item of data) {
        item.diffs = {}
        if (prevItem) {
          for (const [id, asset] of Object.entries(item.assets)) {
            const { name, fullPath, deviceTyp } = asset
            for (const [variable, value] of Object.entries(asset.variables)) {
              const prevAsset = prevItem.assets[id]
              const prevValue = prevAsset ? prevAsset.variables[variable] : null
              if (prevValue !== value) {
                if (!item.diffs[id]) {
                  item.diffs[id] = { name, fullPath, deviceTyp, variables: {} }
                }
                item.diffs[id].variables[variable] = { from: prevValue, to: value }
              }
            }
          }
        }
        prevItem = item
      }
    }
  },
  watch: {
    serverProblem (val) {
      // in case there is any problem with server (SH connection or websocket)
      if (val) {
        this.$store.commit('emptyAssetData')
        this.serverProblemSnackbar = true
      } else {
        this.$store.dispatch('getAssetsInitValues', { visualId: this.id })
        this.serverProblemSnackbar = false
      }
    },
    zoomSlideController () {
      this.visual.zoom = this.zoomSlide
      this.$store.dispatch('updateItem', { modelName: 'visuals', item: this.visual })
    },
    shLog (val) {
      this.shLogUpdated = true
    },

    elements: {
      handler (val) {
        this.initRenStage()
        if (this.$options.renStage) {
          this.$options.renStage.elements = val
        }
      },
      deep: true,
      immediate: true
    },
    backgroundElements (val) {
      this.$options.renStage.backgroundElements = val
    },
    assetData: {
      handler (val) {
        if (this.$options.renStage) {
          this.$options.renStage.assetData = val
        }
      },
      deep: true,
      immediate: true
    },
    selectionIds (val) {
      this.$options.renStage.selectionIds = [...val]
      this.selectedAssetId = this.selectedElement ? this.selectedElement.assetId : null
      if (val.length && this.visualMode === 'command') {
        this.resetCommandTo({ reset: true })
      }
    },
    visualMode (val, prev) {
      if (val === 'watch' && this.websocketOffline) {
        // Back to watch mode
        this.connectWebsocket()
      }
      if (val === 'command') {
        // Command mode selected, add timeout for auto-close
        this.resetCommandTo({ reset: true })
      } else {
        this.resetCommandTo({ reset: false })
      }

      if (this.$options.renStage) {
        if (prev === 'edit') {
          // Unselect any selected elements
          this.selectionIds = []
        }

        this.$options.renStage.visualMode = val
      }

      if (val === 'replay' && this.replay.data.length) { // switching to `replay` and data is already there
        this.replayResetTimeTo(this.replayRange.from) // reset time to `from`
      }
      if (prev === 'replay') {
        this.storeUpdate() // refresh asset elements when leaving `replay` mode
      }
    },
    editMode (val) {
      if (this.$options.renStage) {
        this.$options.renStage.editMode = val
      }
    },
    addingElementTypeName (elementTypeName) {
      const elementType = this.elementTypes.filter(et => et.name === elementTypeName).shift()
      if (elementType) {
        this.$options.renStage && this.$options.renStage.startAddingElementByType(elementType)
        this.$nextTick(() => {
          this.addingElementTypeName = null
        })
      }
    },
    addingConnectedAssetId (id) {
      const asset = this.assets.filter(asset => asset.id === id).shift()
      if (asset) {
        const elementType = this.getCompatibleElementTypes(asset.deviceType).shift()
        if (elementType) {
          this.$options.renStage.startAddingElementByType(elementType, id)
        }
        this.$nextTick(() => {
          this.addingConnectedAssetId = null
        })
      }
    },
    selectedAssetId (id) {
      const { selectedElement, visualMode } = this
      if (selectedElement) {
        const prevAssetId = selectedElement.assetId
        selectedElement.assetId = id || null
        if (visualMode === 'edit' && prevAssetId !== selectedElement.assetId) {
          this.updateManyElements([selectedElement])
        }
      }
    },
    editedLevels () {
      const { levelsInVisuals } = this.visual
      this.editedLevelId = null
      for (let index in levelsInVisuals) {
        if (this.prevEditedLevels[index] === this.editedLevels[index]) {
          this.editedLevels[index] = undefined
        } else {
          this.editedLevelId = levelsInVisuals[index].levelId
        }
      }
      this.prevEditedLevels = [...this.editedLevels]
      this.$options.renStage.levelId = this.editedLevelId
      this.selectionIds = []
    },
    displayedLevels () {
      this.displayedLevelIds = this.visual.levelsInVisuals
        .filter((_, index) => this.displayedLevels[index])
        .map(({ id }) => id)
    },
    zoomSlide (zoomSlide) {
      const convert = number => linearTransform(
        { from: power(0), to: power(100) },
        { from: minZoomLevel, to: maxZoomLevel },
        power(number))
      const { prevZoomSlide } = this
      if (zoomSlide !== prevZoomSlide) {
        this.$options.renStage.performZoom(convert(zoomSlide) / convert(prevZoomSlide), false)
        this.prevZoomSlide = zoomSlide
      }
    },
    sizeAdjustment (sizeAdjustment) {
      this.$options.renStage.setSizeAdjustment(sizeAdjustment)
    },
    replayRange: {
      handler () {
        let { from, to } = this.replayRange
        const now = new Date().getTime()
        if (from > now || to > now) {
          if (from > now) {
            from = now
          }
          if (to > now) {
            to = now
          }
          this.replayRange = { from, to }
        }
        if (from >= to) {
          this.$store.state.snackbar = {
            message: this.$t('invalid-date')
          }
          this.replay.loadEnabled = false
        } else {
          this.replay.loadEnabled = true
        }
      },
      deep: true
    }
  }
}
</script>
